import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Nav, Modal } from 'react-bootstrap';
import Icon from '../UI/Icon';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { getProblemSounds } from '../../services/pronunciationTestService';

function LearningMaterials({ initialCategory = null }) {
  // Initialize selectedCategory with initialCategory if provided
  // Since database uses German categories, use the label directly
  const [selectedCategory, setSelectedCategory] = useState(
    initialCategory || 'all'
  );
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([{ id: 'all', label: 'Alle Materialien' }]);
  const [availableLauts, setAvailableLauts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMovWarning, setShowMovWarning] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getCategoryLabel = (categoryId) => {
    const categoryLabels = {
      'worksheets': 'Arbeitsblätter',
      'cards': 'Kartensätze',
      'videos': 'Videos',
      'mundmotorik': 'Mundmotorik'
    };

    return categoryLabels[categoryId] || categoryId.charAt(0).toUpperCase() + categoryId.slice(1);
  };

  const fetchLearningMaterials = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'learning_materials'));
      const fetchedMaterials = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Only include materials that are visible (visible !== false)
        if (data.visible !== false) {
          fetchedMaterials.push({
            id: doc.id,
            title: data.title,
            category: data.category,
            letters: (data.laut || [])
              .filter(l => typeof l === 'string' && l.length > 0)
              .map(l => l.toUpperCase()),
            description: data.description,
            fileName: data.fileName,
            fileUrl: data.fileUrl,
            size: formatFileSize(data.fileSize),
            fileType: data.fileType
          });
        }
      });
      setMaterials(fetchedMaterials);
      
      // Generate dynamic categories from materials
      const uniqueCategories = [...new Set(fetchedMaterials.map(material => material.category))];
      const dynamicCategories = [
        { id: 'all', label: 'Alle Materialien' },
        ...uniqueCategories.map(categoryId => ({
          id: categoryId,
          label: getCategoryLabel(categoryId)
        }))
      ];
      setCategories(dynamicCategories);
      
      // Extract unique lauts from materials (same logic as MemoryGame)
      const allLautsBeforeFilter = fetchedMaterials
        .filter(material => material.letters && Array.isArray(material.letters)) // Only materials with letters array
        .flatMap(material => material.letters); // Flatten all letters arrays into one array
      
      const lauts = allLautsBeforeFilter
        .map(laut => laut && typeof laut === 'string' ? laut.trim() : laut) // Trim whitespace
        .filter(laut => laut && typeof laut === 'string' && laut !== '') // Remove empty/falsy values
        .filter((laut, index, array) => array.indexOf(laut) === index) // Remove duplicates
        .sort(); // Sort alphabetically
      
      setAvailableLauts(lauts);

      // Auto-select user's severe problem sounds
      const user = auth.currentUser;
      if (user) {
        // Use getProblemSounds service to get sounds from pronunciation tests collection
        const problemSoundsData = await getProblemSounds(user.uid);
        const severeSounds = problemSoundsData.severeProblemSounds;

        if (severeSounds && Array.isArray(severeSounds) && severeSounds.length > 0) {
          // Pre-select severe problem sounds that are available in the materials (case-insensitive)
          const filtersToPreselect = severeSounds.filter(sound =>
            lauts.some(laut => laut.toLowerCase() === sound.toLowerCase())
          ).map(sound => {
            // Return the actual laut from available lauts (with correct casing)
            return lauts.find(laut => laut.toLowerCase() === sound.toLowerCase());
          }).filter(Boolean);

          setSelectedFilters(filtersToPreselect);
        }
      }
    } catch (error) {
      console.error('Error fetching learning materials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLearningMaterials();
  }, []);


  const toggleFilter = (letter) => {
    setSelectedFilters(prev => 
      prev.includes(letter) 
        ? prev.filter(l => l !== letter)
        : [...prev, letter]
    );
  };

  const filteredMaterials = materials.filter(material => {
    const categoryMatch = selectedCategory === 'all' || material.category === selectedCategory;
    const letterMatch = selectedFilters.length === 0 || 
      material.letters.some(letter => selectedFilters.includes(letter));
    return categoryMatch && letterMatch;
  });

  const isMovFile = (material) => {
    // Check if file is .mov based on fileName or fileType
    const fileName = material.fileName || '';
    const fileType = material.fileType || '';
    return fileName.toLowerCase().endsWith('.mov') || fileType.toLowerCase().includes('quicktime');
  };

  const handleDownload = (material) => {
    // Check if it's a video and it's a .mov file
    const isVideo = material.category === 'videos' || material.fileType?.startsWith('video/');
    
    // Track video view in localStorage
    if (isVideo) {
      const watchedVideos = JSON.parse(localStorage.getItem('watchedVideos') || '[]');
      const videoData = {
        id: material.id,
        title: material.title,
        fileUrl: material.fileUrl,
        size: material.size,
        timestamp: new Date().toISOString()
      };
      
      // Remove if already exists to avoid duplicates
      const filtered = watchedVideos.filter(v => v.id !== material.id);
      // Add to front and keep only last 3
      const updated = [videoData, ...filtered].slice(0, 3);
      localStorage.setItem('watchedVideos', JSON.stringify(updated));
    }
    
    if (isVideo && isMovFile(material)) {
      // Show warning modal for .mov files
      setPendingDownload(material);
      setShowMovWarning(true);
    } else {
      // Proceed with opening/downloading
      window.open(material.fileUrl, '_blank');
    }
  };

  const handleConfirmDownload = () => {
    if (pendingDownload) {
      window.open(pendingDownload.fileUrl, '_blank');
    }
    setShowMovWarning(false);
    setPendingDownload(null);
  };

  const handleCancelDownload = () => {
    setShowMovWarning(false);
    setPendingDownload(null);
  };


  return (
    <>
      {/* MOV File Warning Modal */}
      <Modal show={showMovWarning} onHide={handleCancelDownload} centered>
        <Modal.Header closeButton style={{ backgroundColor: 'white' }}>
          <Modal.Title style={{ fontWeight: 'bold' }}>
            Video-Download Hinweis
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Dieses Video liegt im .MOV-Format vor und kann möglicherweise nicht direkt im Browser abgespielt werden.
          </p>
          <p className="mb-0">
            <strong>Das Video wird heruntergeladen</strong> und kann dann mit einem Video-Player auf Ihrem Gerät geöffnet werden (z.B. VLC Media Player, QuickTime, Windows Media Player).
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDownload}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleConfirmDownload}>
            <Icon name="download" size="sm" className="me-2" />
            Jetzt herunterladen
          </Button>
        </Modal.Footer>
      </Modal>

      <Container>
        <div className="mb-5">
          <h2>Lernmaterialien</h2>
          <p className="text-secondary">
            Lade kostenlose Übungsmaterialien für zu Hause herunter
          </p>
        </div>

      <Nav variant="tabs" className="mb-4">
        {categories.map(category => (
          <Nav.Item key={category.id}>
            <Nav.Link
              active={selectedCategory === category.id}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <div className="filter-container mb-4">
        <h5 className="mb-3">Filter nach Lauten:</h5>
        <div className="letter-filters">
          {availableLauts.map(laut => (
            <Button
              key={laut}
              variant={selectedFilters.includes(laut) ? "primary" : "outline-secondary"}
              size="sm"
              className="letter-filter-btn"
              onClick={() => toggleFilter(laut)}
            >
              {laut}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <p className="text-muted">Lade Materialien...</p>
        </div>
      ) : (
        <Row className="g-4">
          {filteredMaterials.map(material => (
          <Col key={material.id} md={6} lg={4}>
            <Card className="material-card h-100">
              <Card.Body className="d-flex flex-column">
                
                <h5 className="mb-2">{material.title}</h5>
                <p className="text-secondary small flex-grow-1 mb-3">
                  {material.description}
                </p>
                
                <div className="mt-auto">
                  <div className="d-flex justify-content-between align-items-center mb-3 text-muted small">
                    <span>
                      <Icon name={material.category === 'videos' || material.fileType?.startsWith('video/') ? "video" : "document"} size="xs" className="me-1" />
                      {material.letters.length > 0 ? (
                        <span>
                          {material.letters.map((letter, index) => (
                            <span key={letter}>
                              <span className="letter-badge-inline">{letter}</span>
                              {index < material.letters.length - 1 ? ', ' : ''}
                            </span>
                          ))}
                        </span>
                      ) : (
                        'Allgemein'
                      )}
                    </span>
                    <span>{material.size}</span>
                  </div>
                  
                  <Button 
                    variant="primary"
                    className="w-100"
                    onClick={() => handleDownload(material)}
                  >
                    <Icon name={material.category === 'videos' || material.fileType?.startsWith('video/') ? "video" : "download"} size="sm" className="me-2" />
                    {material.category === 'videos' || material.fileType?.startsWith('video/') ? 'Abspielen' : 'Herunterladen'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      )}

      {!loading && filteredMaterials.length === 0 && (
        <div className="text-center py-5">
          <p className="text-muted">
            Keine Materialien für die ausgewählten Filter gefunden.
          </p>
        </div>
      )}

      <div className="text-center mt-5 p-4 bg-light rounded">
        <h5>Hinweis</h5>
        <p className="mb-0">
          Die Arbeitsblätter wurden für Sie von Therapeut:innen entwickelt und sind urheberrechtlich geschützt. Eine Weitergabe oder Vervielfältigung ist nicht gestattet.
        </p>
      </div>
    </Container>
    </>
  );
}

export default LearningMaterials;